import { useLocation, useParams } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import Navbar from "../../Common/Navbar";
import { Footer } from "../../Footer/Footer";
import { useSelector } from "react-redux";
import { fetchApi } from "../../utlis/axios";
import { useDispatch } from "react-redux";
import JoditEditor from "jodit-react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import Loader from "../../Common/Loader";
import { toast } from "react-toastify";

const BlogDetails = () => {
  const { state } = useLocation();
  const theme = "light";
  const { userInfo } = useSelector((state) => state?.authSlice || {});
  const [blog, setBlog] = useState(state?.blog);
  const [blogId, setBlogId] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [content, setContent] = useState(state?.blog?.description || "");
  const [title, setTitle] = useState(state?.blog?.title || "");
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();
  const url = process.env.REACT_APP_API_URL;
  const editor = useRef(null);
  const blogdetails = state?.blog;
  const dispatch = useDispatch();

  const location = useLocation();

  useEffect(() => {
    try {
      const queryParams = new URLSearchParams(location.search);
      const blogId = queryParams.get("blogId");

      if (!blogId) {
        console.error("No blogId found in URL");
        return;
      }

      setBlogId(blogId);
      if (!state) {
        fetchBlog(blogId);
      }
    } catch (error) {
      console.error("Error parsing URL parameters:", error);
    }
  }, [location.search]);

  useEffect(() => {
    setBlog(state?.blog);
  }, [state?.blog]);

  const { slug } = useParams();
  useEffect(() => {
    // If we're on the panama-schedule page without a blogId in the URL
    const searchParams = new URLSearchParams(location.search);
    const blogId = searchParams.get("blogId");
    if (slug === "panama-schedule" && !blogId) {
      // Redirect to the full correct URL
      navigate(
        `/blog-details/panama-schedule?blogId=7816693a-8d44-4ea9-b120-f11308521bdd`,
        { replace: true }
      );
    }
  }, [slug, location.search, navigate]);

  function formatDate(isoString) {
    const date = new Date(isoString);
    const options = { year: "numeric", month: "long", day: "numeric" };
    return date.toLocaleDateString("en-US", options);
  }

  function getReadingTime(html) {
    const plainText = html.replace(/<[^>]+>/g, ""); // Remove HTML tags
    const wordCount = plainText.trim().split(/\s+/).length;
    const minutes = Math.ceil(wordCount / 200); // 200 words per minute
    return minutes;
  }

  const fetchBlog = async (blogId) => {
    setLoading(true);
    try {
      const response = await fetchApi.get(`blogs/${blogId}/`);
      if (response.status === 200) {
        setBlog(response.data);
      } else {
        console.error("Failed to fetch blog:", response.status);
      }
    } catch (error) {
      console.error("Error fetching blog:", error);
    } finally {
      setLoading(false);
    }
  };

  const [isSharing, setIsSharing] = useState(false);

  const handleShare = async () => {
    if (isSharing) return; // Prevent multiple simultaneous shares

    setIsSharing(true);
    try {
      const blogUrl = window.location.href;
      const shareData = {
        title: blog?.title,
        text: blog?.description?.substring(0, 100) + "...",
        url: blogUrl,
      };

      // Try native share API first
      if (navigator.share) {
        await navigator.share(shareData);
        await incrementShareCount(); // Only increment after successful share
      } else {
        // Fallback: Copy to clipboard
        await navigator.clipboard.writeText(blogUrl);
        toast.success("Text copied to clipboard");
        await incrementShareCount(); // Increment for clipboard shares too
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error sharing:", error);
        toast.error("Failed to share");
      }
    } finally {
      setIsSharing(false);
    }
  };

  const incrementShareCount = async () => {
    try {
      const response = await fetchApi.patch(
        `blogs/${blog?.id}/`,
        { share_count: (blog?.share_count || 0) + 1 },
        {
          headers: {
            Authorization: `Bearer ${userInfo?.access}`,
          },
        }
      );

      if (response.status !== 200) {
        throw new Error("Failed to update share count");
      }
    } catch (error) {
      console.error("Error updating share count:", error);
      // Don't show error to user since sharing worked, just log it
    }
  };

  const editorConfig = {
    readonly: false,
    theme: theme === "dark" ? "dark" : "default", // Theme support for Jodit
    uploader: {
      insertImageAsBase64URI: false,
      url: `${url}attachments/`,
      format: "json",
      method: "POST",
      headers: {
        Authorization: `Bearer ${userInfo?.access}`,
      },
      filesVariableName: () => "file",
      prepareData: (formData) => {
        formData.append("created_by", userInfo?.user.id);
        return formData;
      },
      isSuccess: (resp) => resp?.file,
      process: (resp) => ({
        files: [resp.file],
        path: resp.file,
        baseurl: url,
      }),
      defaultHandlerSuccess: function (data) {
        if (data.files && data.files.length) {
          const jodit = this;
          data.files.forEach((file) => {
            if (file.match(/\.(jpeg|jpg|gif|png)$/) != null) {
              jodit.s.insertImage(file);
            } else {
              const fileName = file.split("/").pop();
              jodit.s.insertHTML(`<a href="${file}" download>${fileName}</a>`);
            }
          });
          return false;
        }
        return data.path;
      },
      error: (e) => {
        toast.error("File upload failed");
        return false;
      },
    },
    buttons: [
      "bold",
      "italic",
      "underline",
      "|",
      "ul",
      "ol",
      "|",
      "image",
      "link",
      "|",
      "left",
      "center",
      "right",
      "justify",
      "|",
      "undo",
      "redo",
    ],
  };

  const handleUpdate = async () => {
    try {
      setIsUpdating(true);
      const response = await fetchApi.patch(
        `blogs/${blog?.id}/`,
        { title: title, description: content },
        {
          headers: {
            Authorization: `Bearer ${userInfo?.access}`,
          },
        }
      );

      if (response.status === 200) {
        setIsEditing(false);

        setIsEditing(false);
        setBlog({ ...blogdetails, description: content, title: title });
      } else {
        throw new Error("Failed to update blog");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      const response = await fetchApi.delete(`blogs/${blog?.id}/`, {
        headers: {
          Authorization: `Bearer ${userInfo?.access}`,
        },
      });

      if (response.status === 204) {
        navigate(-1);
      } else {
        toast.error("Failed to delete");
      }
    } catch (error) {
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div>
      {/* React Helmet for managing document head */}
      <Helmet>
        <title>{`${blog?.title} | KWF`}</title>
        <meta
          name="description"
          content={
            blog?.tags?.meta_description
              ? blog?.tags?.meta_description
              : "not found"
          }
        />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={blog?.title} />
        <meta
          property="og:description"
          content={
            blog?.tags?.meta_description
              ? blog?.tags?.meta_description
              : "not found"
          }
        />
        {blog?.attachments?.[0]?.file && (
          <meta property="og:image" content={blog?.attachments?.[0]?.file} />
        )}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog?.title} />
        <meta
          name="twitter:description"
          content={
            blog?.tags?.meta_description
              ? blog?.tags?.meta_description
              : "not found"
          }
        />
        {blog?.attachments?.[0]?.file && (
          <meta name="twitter:image" content={blog?.attachments?.[0]?.file} />
        )}

        {blog?.tags?.tags?.length > 0 && (
          <meta name="keywords" content={blog.tags.tags.join(", ")} />
        )}
        <meta name="author" content={"BugTrackin"} />
        <link
          rel="canonical"
          href={`${window.location.origin}/blog-details/${blog?.tags?.blog_url}?blogId=${blog?.id}`}
        />
      </Helmet>

      <Navbar parent={"family"} />
      {isLoading && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-10 lg:mt-[70px] mt-[52px] "></div>
          <div className="fixed inset-0 flex items-center justify-center z-20 lg:mt-[70px] mt-[52px]">
            <Loader />
          </div>
        </>
      )}
      <div
        className={`max-w-screen-lg w-full py-8 md:px-8 px-[16px] md:mt-16 mt-10 mx-auto`}
      >
        <div className="w-full">
          <img
            src={blog?.attachments?.[0]?.file}
            className="w-full md:h-96 sm:h-72 h-56 object-cover"
            alt={blog?.tags?.alt_text ? blog?.tags?.alt_text : blog?.title}
          />

          <div className="flex items-center gap-4 justify-between">
            <div className="flex items-center gap-2 mt-6">
              <span className="py-1.5 px-3 rounded-full flex items-center gap-2 border border-gray-400">
                <span className="w-2 h-2 rounded-full bg-primary-green" />
                {blog?.tags?.keywords?.[0]}
              </span>
              <span
                className={`text-sm flex items-center text-gray-600 font-medium`}
              >
                Published on {formatDate(blog?.created_at)},{" "}
                {blog?.description ? getReadingTime(blog.description) : 0} min
                read
              </span>
            </div>

            <div className="flex items-center gap-4">
              {/* <button
                onClick={handleShare}
                className="heroButton flex items-center justify-center gap-2"
                style={{ display: "flex", alignItems: "center" }}
              >
                <MdShare className="w-4 h-4" />
                Share ({blog?.share_count || 0})
              </button> */}
              {(userInfo?.user?.role === "admin" ||
                userInfo?.user?.role === "medicalstaff") && (
                <button
                  className="primary-button font-medium"
                  onClick={() => {
                    if (isEditing) {
                      handleUpdate();
                    } else {
                      setTitle(blog?.title);
                      setContent(blog?.description);
                      setIsEditing(true);
                    }
                  }}
                  disabled={isUpdating}
                >
                  {isEditing ? (isUpdating ? "Saving..." : "Save") : "Edit"}
                </button>
              )}
              {(userInfo?.user?.role === "admin" ||
                userInfo?.user?.role === "medicalstaff") && (
                <button
                  className="w-max primary-button font-medium"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              )}
            </div>
          </div>
          {isEditing ? (
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={`w-full mt-2 p-2 rounded-md text-4xl border-none bg-transparent font-bold fontRoboto`}
            />
          ) : (
            <h1
              className={`text-4xl font-bold mt-2 fontRoboto ${
                theme === "dark" ? "text-white" : "text-black"
              }`}
            >
              {blog?.title}
            </h1>
          )}
          {isEditing ? (
            <div className="mt-6 w-full">
              <JoditEditor
                ref={editor}
                value={content}
                config={editorConfig}
                tabIndex={1}
                onBlur={(newContent) => setContent(newContent)}
              />
            </div>
          ) : (
            <div
              className={`mt-6 ${
                theme === "dark" ? "text-gray-200" : "text-gray-700"
              }`}
              dangerouslySetInnerHTML={{ __html: blog?.description }}
            ></div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default BlogDetails;
